import { HTTP_INTERCEPTORS, provideHttpClient, withFetch } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, inject, isDevMode } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { InMemoryScrollingFeature, InMemoryScrollingOptions, provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading, withViewTransitions } from '@angular/router';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { catchError, firstValueFrom, of } from 'rxjs';
import { appRoutes } from 'app/app.routing';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { ApolloLink, DefaultOptions, InMemoryCache } from '@apollo/client';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { environment } from 'environments/environment';
import { GlobalErrorHandlerService } from './shared/common/config/config-cloud-watch/global-error-handler.service';
import { HttpInterceptorService } from './shared/common/utils/http/HttpInterceptor.service';
import { DialPhoneService } from './shared/service/dialphone.service';
import { FuseConfigModule } from '@fuse/services/config';
import { appConfigFuse } from './core/config/app.config.fuse';
import { provideServiceWorker } from '@angular/service-worker';
import { ToastrModule } from 'ngx-toastr';
import { BasedPreloadingStrategy } from './app.preload';
import { provideQuillConfig } from 'ngx-quill';
import { AuthConfig, OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';

const scrollConfig: InMemoryScrollingOptions = {
    scrollPositionRestoration: 'top',
    anchorScrolling: 'enabled',
};

const withInMemoryScrollingFeature: InMemoryScrollingFeature =
    withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withFetch()),
        provideRouter(
            appRoutes,
            withInMemoryScrollingFeature,
            withViewTransitions(),
            withComponentInputBinding(),
            withPreloading(BasedPreloadingStrategy),
        ),
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English'
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true
            },
            loader: TranslocoHttpLoader
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = 'en';
                translocoService.setActiveLang(defaultLang);

                return () => {
                    // Try to load translations, but don't fail if unavailable
                    const loadPromise = firstValueFrom(
                        translocoService.load(defaultLang).pipe(
                            catchError(() => of({})) // Return empty object on error
                        )
                    );

                    // Set a timeout to prevent hanging
                    const timeoutPromise = new Promise(resolve =>
                        setTimeout(() => resolve({}), 3000)
                    );

                    return Promise.race([loadPromise, timeoutPromise]);
                };
            },
            multi: true,
        },
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:5000',
        }),
        importProvidersFrom(
            FuseConfigModule.forRoot(appConfigFuse),
            ToastrModule.forRoot({
                preventDuplicates: true,
                maxOpened: 1
            }),
            OAuthModule.forRoot()
        ),
        {
            provide: APP_INITIALIZER,
            useFactory: (dps: DialPhoneService) => () => dps.loadDialPhoneData(),
            deps: [DialPhoneService],
            multi: true
        },
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpInterceptorService,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandlerService
        },
        {
            provide: OAuthStorage,
            useValue: sessionStorage
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (oauthService: OAuthService) => {
                // Configure OAuth IMMEDIATELY when app starts
                const authConfig: AuthConfig = {
                    issuer: environment.zitadel.issuer,
                    redirectUri: environment.zitadel.redirectUri,
                    postLogoutRedirectUri: environment.zitadel.postLogoutRedirectUri,
                    clientId: environment.zitadel.clientId,
                    responseType: environment.zitadel.responseType,
                    scope: environment.zitadel.scope,
                    showDebugInformation: environment.zitadel.showDebugInformation,
                    strictDiscoveryDocumentValidation: false,
                    skipIssuerCheck: true,
                    requireHttps: false,
                    oidc: true,
                    // ONLY if Zitadel requires client_secret (not recommended for SPAs)
                    // dummyClientSecret: 'YOUR_CLIENT_SECRET_FROM_ZITADEL_ADMIN'
                };
                oauthService.configure(authConfig);
                // console.log('✅ OAuth configured in APP_INITIALIZER:', oauthService.issuer, oauthService.redirectUri);

                return () => Promise.resolve();
            },
            deps: [OAuthService],
            multi: true
        },
        // {
        //     provide: AWS_STREAM_CONFIG,
        //     useValue: {
        //         logGroupName: environment.awsCloudWatch.logGroupName,
        //         logStreamName: environment.awsCloudWatch.logStreamName,
        //     }
        // },
        // {
        //     provide: AWS_CLOUD_WATCH_CLIENT_CONFIG,
        //     useValue: {
        //         awsAccessKey: environment.awsCloudWatch.awsAccessKey,
        //         awsSecretKey: environment.awsCloudWatch.awsSecretKey,
        //         awsRegion: environment.awsCloudWatch.awsRegion
        //     }
        // },
        {
            provide: APOLLO_OPTIONS,
            useFactory(httpLink: HttpLink) {
                const http = httpLink.create({ uri: environment.graphql.link });
                const middleware = new ApolloLink((operation, forward) => {
                    operation.setContext({
                        // headers: new HttpHeaders().set(
                        //     'x-hasura-admin-secret', `${environment.graphql.key}`,
                        // ),
                    });
                    return forward(operation);
                });

                // Add error handling for GraphQL
                const errorLink = new ApolloLink((operation, forward) => {
                    return forward(operation).map(response => {
                        if (response.errors) {
                            console.warn('GraphQL errors:', response.errors);
                        }
                        return response;
                    });
                });

                const defaultOptions: DefaultOptions = {
                    watchQuery: {
                        fetchPolicy: 'no-cache',
                        errorPolicy: 'ignore',
                    },
                    query: {
                        fetchPolicy: 'no-cache',
                        errorPolicy: 'all',
                    },
                }

                const link = errorLink.concat(middleware).concat(http);
                return {
                    link,
                    cache: new InMemoryCache(),
                    defaultOptions: defaultOptions
                };
            },
            deps: [HttpLink],
        },
        Apollo,
        provideQuillConfig({
            modules: {
                toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    ['blockquote', 'code-block'],
                    [{ 'header': 1 }, { 'header': 2 }],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                    [{ 'script': 'sub' }, { 'script': 'super' }],
                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                    [{ 'direction': 'rtl' }],
                    [{ 'size': ['small', false, 'large', 'huge'] }],
                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'font': [] }],
                    [{ 'align': [] }],
                    ['clean'],
                    ['link', 'image', 'video']
                ]
            }
        })
    ]
}